import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDownIcon, PrintIcon } from "../../../../Icons";
import { APPLICATION_THEME } from "../../applicationTheme";
import {
  CLINICAL_FORM_MARKS,
  selectionMarkMode,
} from "../../../Patients/components/clinicalForms/clinicalFormMarks";

const MENU_WIDTH = 224;

/**
 * Bulk actions for General Forms tables (Print + optional flags).
 * Menu is portaled so it is not clipped by the list card overflow.
 */
const GeneralFormsBulkActions = ({
  selectedCount,
  selectedRows = [],
  disabled = false,
  loading = false,
  hasFlags = false,
  canManageFlags = false,
  onPrint,
  onApplyMark,
  onRemoveMark,
}) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const updatePosition = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const gap = 6;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < 240 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(360, Math.max(160, openUp ? spaceAbove : spaceBelow));
    const left = Math.min(
      Math.max(8, rect.right - MENU_WIDTH),
      Math.max(8, window.innerWidth - MENU_WIDTH - 8),
    );
    setMenuStyle({
      position: "fixed",
      left,
      width: MENU_WIDTH,
      zIndex: 80,
      maxHeight,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + gap }
        : { top: rect.bottom + gap }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (
        menuRef.current?.contains(e.target) ||
        buttonRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const isDisabled = disabled || loading || selectedCount === 0;

  useEffect(() => {
    if (isDisabled) setOpen(false);
  }, [isDisabled]);

  const selectedIds = new Set((selectedRows || []).map((row) => row.id));

  const menu =
    open && menuStyle && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={menuStyle}
            className={`overflow-y-auto rounded-lg border bg-white py-1 shadow-lg ${APPLICATION_THEME.section.wrap}`}
          >
            <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              Select an Action
            </p>
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-sm text-slate-800 hover:bg-indigo-50"
              onClick={async () => {
                setOpen(false);
                await onPrint?.();
              }}
            >
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-slate-700 text-white shrink-0">
                <PrintIcon size={12} strokeWidth={2.25} />
              </span>
              Print
            </button>
            {hasFlags && canManageFlags
              ? (() => {
                  const markModes = CLINICAL_FORM_MARKS.map((mark) => ({
                    mark,
                    mode: selectionMarkMode(selectedRows, selectedIds, mark.type),
                  }));
                  const applyMarks = markModes.filter(
                    ({ mode }) => mode === "apply" || mode === "both",
                  );
                  const removeMarks = markModes.filter(
                    ({ mode }) => mode === "remove" || mode === "both",
                  );
                  const renderMarkButton = (mark, action) => {
                    const Icon = mark.Icon;
                    return (
                      <button
                        key={`${mark.type}-${action}`}
                        type="button"
                        role="menuitem"
                        onClick={async () => {
                          setOpen(false);
                          if (action === "remove") await onRemoveMark?.(mark.type);
                          else await onApplyMark?.(mark.type);
                        }}
                        className="w-full flex items-center gap-2 px-3.5 py-2 text-left text-sm text-slate-800 hover:bg-indigo-50"
                      >
                        <span
                          className="inline-flex h-5 w-5 items-center justify-center rounded-md text-white shrink-0"
                          style={{ backgroundColor: mark.color }}
                        >
                          {Icon ? (
                            <Icon size={12} strokeWidth={2.25} />
                          ) : (
                            mark.code
                          )}
                        </span>
                        {mark.label}
                      </button>
                    );
                  };
                  return (
                    <>
                      {applyMarks.length > 0 ? (
                        <>
                          <div className="my-1 border-t border-slate-100" />
                          <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                            Mark
                          </p>
                          {applyMarks.map(({ mark }) =>
                            renderMarkButton(mark, "apply"),
                          )}
                        </>
                      ) : null}
                      {removeMarks.length > 0 ? (
                        <>
                          <div className="my-1 border-t border-slate-100" />
                          <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                            Remove
                          </p>
                          {removeMarks.map(({ mark }) =>
                            renderMarkButton(mark, "remove"),
                          )}
                        </>
                      ) : null}
                    </>
                  );
                })()
              : null}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-sm text-slate-600">
        {selectedCount > 0
          ? `${selectedCount} selected`
          : "Select forms to enable actions"}
      </p>
      <div>
        <button
          ref={buttonRef}
          type="button"
          disabled={isDisabled}
          onClick={() => setOpen((v) => !v)}
          className={`inline-flex items-center justify-center gap-1.5 h-8 px-3 text-sm font-medium rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${APPLICATION_THEME.button.outline}`}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          Actions
          <ChevronDownIcon
            size={14}
            className={`text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
        {menu}
      </div>
    </div>
  );
};

export default GeneralFormsBulkActions;
